'use client';

import SettingsQuestionnaireModal from '@/components/SettingsQuestionnaireModal';
import { createClientLogger } from '@/client/logging/client-logger';
import { SettingsTabId } from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

type SettingsModalContextValue = {
    openSettingsModal: () => void;
    openBillingTopUp: () => void;
};

const settingsModalLogger = createClientLogger({
    scope: 'settings-modal',
});

const missingProviderContext: SettingsModalContextValue = {
    openSettingsModal: () => {
        settingsModalLogger.warn('[client.settings-modal.open] Skipped settings modal open without provider.', {
            outcome: 'missing_provider',
        });
    },
    openBillingTopUp: () => {
        settingsModalLogger.warn('[client.settings-modal.billing-top-up] Skipped top-up open without provider.', {
            outcome: 'missing_provider',
        });
    },
};

const SettingsModalContext = createContext<SettingsModalContextValue>(missingProviderContext);

export const useSettingsModal = (): SettingsModalContextValue => useContext(SettingsModalContext);

export const SettingsModalProvider = ({ children }: PropsWithChildren) => {
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [initialTab, setInitialTab] = useState<SettingsTabId>('general');
    const [paymentRequestKey, setPaymentRequestKey] = useState(0);

    const openSettingsModal = useCallback(() => {
        setInitialTab('general');
        setIsSettingsModalOpen(true);
    }, []);

    const openBillingTopUp = useCallback(() => {
        settingsModalLogger.info('[client.settings-modal.billing-top-up] Accepted top-up open request.', {
            outcome: 'accepted',
        });
        setInitialTab('limits-payments');
        setPaymentRequestKey((currentKey) => currentKey + 1);
        setIsSettingsModalOpen(true);
    }, []);

    const value = useMemo(
        () => ({
            openSettingsModal,
            openBillingTopUp,
        }),
        [openBillingTopUp, openSettingsModal],
    );

    return (
        <SettingsModalContext.Provider value={value}>
            {children}
            <SettingsQuestionnaireModal
                isShown={isSettingsModalOpen}
                setIsShown={setIsSettingsModalOpen}
                initialTab={initialTab}
                openPaymentRequestKey={paymentRequestKey}
            />
        </SettingsModalContext.Provider>
    );
};
